import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import apiService from '../../../services/api';
import logger from '../../../utils/logging/logger';
import { buildQnaRows, buildExtensionRows, buildGradingRows } from './instructorHome';

const QNA_POLL_MS = 30000;

/**
 * Data for the three lanes. `liveSessions` = today's sessions that are on now
 * (from useInstructorActiveAttendanceSessions); their Q&A is polled while live.
 */
export default function useInstructorLanes({ enabled, liveSessions, activeCourseIds = [] }) {
  const [extensions, setExtensions] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [threads, setThreads] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const loadWork = useCallback(async () => {
    const [ext, asg, qz] = await Promise.allSettled([
      apiService.listExtensions({ status: 'pending' }),
      apiService.getAssignments(),
      apiService.request('/quizzes/assigned?limit=1000'),
    ]);
    if (ext.status === 'fulfilled' && ext.value?.success) setExtensions(ext.value.requests || []);
    if (asg.status === 'fulfilled' && asg.value?.success) setAssignments(asg.value.assignments || []);
    if (qz.status === 'fulfilled' && qz.value?.success) setQuizzes(qz.value.data || []);
    [ext, asg, qz].forEach((r) => {
      if (r.status === 'rejected') logger.warn('Instructor dashboard: a lane failed to load', { error: r.reason });
    });
    setLoaded(true);
  }, []);

  useEffect(() => {
    if (enabled) loadWork();
  }, [enabled, loadWork]);

  // Live Q&A: poll the live sessions' threads.
  const sessionKey = (liveSessions || []).map((s) => s.session_id).join(',');
  const sessionsRef = useRef(liveSessions);
  sessionsRef.current = liveSessions;
  const loadQna = useCallback(async () => {
    const sessions = sessionsRef.current || [];
    if (!sessions.length) { setThreads([]); return; }
    const results = await Promise.allSettled(sessions.map((s) => apiService.getSessionQna(s.session_id)));
    setThreads(results
      .map((r, i) => (r.status === 'fulfilled' && r.value?.success ? { session: sessions[i], questions: r.value.questions || [] } : null))
      .filter(Boolean));
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    loadQna();
    if (!sessionKey) return undefined;
    const t = setInterval(loadQna, QNA_POLL_MS);
    return () => clearInterval(t);
  }, [enabled, sessionKey, loadQna]);

  const lanes = useMemo(() => ({
    qna: buildQnaRows(threads),
    extensions: buildExtensionRows(extensions),
    // Only active courses: grading left over in ended or switched-off courses stays off the dashboard.
    grading: buildGradingRows(assignments, quizzes).filter((r) => activeCourseIds.includes(Number(r.courseId))),
  }), [threads, extensions, assignments, quizzes, activeCourseIds]);

  return { lanes, loaded, reloadWork: loadWork, reloadQna: loadQna };
}
